import { memo } from 'react';
import { JuryVote, AppealStatus } from '../../types/appeals';
import { PlayerProfile } from '../PlayerProfile';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, XCircle, Clock, Users, Gavel, Bot, Info } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';

interface JuryVotesSectionProps {
  votes: JuryVote[];
  appealStatus?: AppealStatus;
  className?: string;
}

export const JuryVotesSection = memo(({ votes, appealStatus, className }: JuryVotesSectionProps) => {
  const isInJury = appealStatus === 'in_jury';
  const isJuryDecided = appealStatus === 'jury_decided';
  const isStaffAnswered = appealStatus === 'staff_answered';

  if (!votes || votes.length === 0) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)}>
        <SectionHeader title="Jury Votes" icon={Gavel} iconClassName="text-purple-400" />
        {isStaffAnswered ? (
          <div className="alert bg-purple-500/10 border-purple-500/25" role="status">
            <Bot className="text-purple-400" />
            <div>
              <p className="text-sm text-purple-300 font-medium">Waiting for Mr.Roboto</p>
              <p className="text-xs text-glass-400 mt-0.5">
                Mr.Roboto will automatically create a jury case and select jurors.
                This typically happens within a few minutes.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-glass-400">No jury has been selected yet.</p>
        )}
      </div>
    );
  }

  const votedCount = votes.filter(v => v.vote !== null).length;
  const guiltyCount = votes.filter(v => v.vote === 'guilty').length;
  const notGuiltyCount = votes.filter(v => v.vote === 'not_guilty').length;
  const pendingCount = votes.filter(v => v.vote === null).length;

  return (
    <div className={cn('glass-card p-4 md:p-5 space-y-4', className)}>
      <SectionHeader
        className="mb-0"
        title={
          <span className="inline-flex items-center gap-2">
            Jury Votes
            {(isInJury || isJuryDecided) && (
              <span className="badge badge-purple">
                <Bot className="w-3 h-3" />
                Mr.Roboto
              </span>
            )}
          </span>
        }
        icon={Gavel}
        iconClassName="text-purple-400"
        actions={
          <span className="text-xs text-glass-400 tabular-nums inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {votedCount}/{votes.length} voted
          </span>
        }
      />

      {isInJury && (
        <div className="alert bg-purple-500/10 border-purple-500/25" role="status">
          <Info className="text-purple-400" />
          <p className="text-xs text-glass-400">
            Jury members are voting in Discord via Mr.Roboto. Votes appear here in real-time as they are cast.
            Mr.Roboto will finalize the verdict once enough votes are in.
            Staff cannot influence the vote — only escalate or cancel if needed.
          </p>
        </div>
      )}

      {isJuryDecided && (
        <div className="alert alert-info" role="status">
          <Info />
          <p className="text-xs text-glass-400">
            The jury has reached a verdict. Mr.Roboto is finalizing the decision and will update the appeal status shortly.
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-center">
          <p className="text-xl font-semibold tabular-nums text-red-400">{guiltyCount}</p>
          <p className="text-xs text-glass-400">Guilty</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-center">
          <p className="text-xl font-semibold tabular-nums text-green-400">{notGuiltyCount}</p>
          <p className="text-xs text-glass-400">Not Guilty</p>
        </div>
        <div className="subcard p-2 text-center">
          <p className="text-xl font-semibold tabular-nums text-gray-400">{pendingCount}</p>
          <p className="text-xs text-glass-400">Pending</p>
        </div>
      </div>

      {votedCount > 0 && (
        <div className="w-full bg-glass-100/10 rounded-full h-2 overflow-hidden flex">
          {guiltyCount > 0 && (
            <div className="bg-red-500 h-full" style={{ width: `${(guiltyCount / votes.length) * 100}%` }} />
          )}
          {notGuiltyCount > 0 && (
            <div className="bg-green-500 h-full" style={{ width: `${(notGuiltyCount / votes.length) * 100}%` }} />
          )}
        </div>
      )}

      <div className="divide-y divide-glass-200/10">
        {votes.map(vote => (
          <div key={vote.id} className="flex items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2 min-w-0">
              {vote.vote === 'guilty' && <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
              {vote.vote === 'not_guilty' && <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />}
              {vote.vote === null && <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              <div className="min-w-0">
                {vote.juror_minecraft_uuid ? (
                  <PlayerProfile uuid={vote.juror_minecraft_uuid} inline showUuid={false} />
                ) : (
                  <span className="text-sm text-glass-300 font-mono">{vote.juror_discord_id}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-glass-400 flex-shrink-0">
              <span className={cn(
                'font-semibold capitalize',
                vote.vote === 'guilty' ? 'text-red-400' :
                vote.vote === 'not_guilty' ? 'text-green-400' :
                'text-gray-400'
              )}>
                {vote.vote ? vote.vote.replace('_', ' ') : 'Pending'}
              </span>
              {vote.voted_at && (
                <span className="tabular-nums">{formatDistanceToNow(new Date(vote.voted_at), { addSuffix: true })}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});
